/** Relations row → **Media** (plan D11): the photos and videos shared in the tour's chats
 *  (`/tours/<id>/media/`, the dashboard's tour media), newest first, in a drawer.
 *
 *  The project's image rule: every tile is a DOWNSIZED thumbnail from the resize service
 *  (`IMAGE_PRESET.THUMB`); the original opens only on an explicit click, in a new tab. A
 *  video has no thumbnail — a video icon and the word "Video", the file on click. */
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import * as toursApi from '@/api/tours';
import type { TourDetail, TourMediaItem } from '@/api/tours';
import { RELATION_PAGE_SIZE, RelationDrawer, RelationFailure, RelationLink, RelationPager } from '@/features/records/relations/RelationDrawer';
import { usePermissions } from '@/hooks/usePermissions';
import { formatRelativeTime } from '@/utils/format';
import { IMAGE_PRESET, imageUrl } from '@/utils/imageUrl';

export const tourMediaKey = (tourId: number, page: number) => ['tours', tourId, 'media', page] as const;

export function TourMediaRelation({ tour }: { tour: TourDetail }) {
  const { hasPerm } = usePermissions();
  // Guests' and staff's chat photos are booking-level data: console_bookings_view too.
  const allowed = hasPerm('console_bookings_view');
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(1);
  const first = useQuery({
    queryKey: tourMediaKey(tour.id, 1),
    queryFn: () => toursApi.listTourMedia(tour.id, 1, RELATION_PAGE_SIZE),
    enabled: allowed,
  });
  if (!allowed) {
    return (
      <Tooltip title="The photos and videos shared in the tour’s chats are guest data — they need console_bookings_view">
        <Typography component="span" variant="body2" color="text.secondary" tabIndex={0} data-media-locked sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <LockOutlinedIcon aria-hidden sx={{ fontSize: 14 }} />
          Media · needs console_bookings_view
        </Typography>
      </Tooltip>
    );
  }
  return (
    <>
      <RelationLink
        label="Media"
        count={first.data ? first.data.count : null}
        failed={first.isError}
        onOpen={() => {
          setPage(1);
          setOpen(true);
        }}
      />
      <RelationDrawer open={open} onClose={() => setOpen(false)} title={`Media of ${tour.name}`}>
        {open && <MediaGrid tour={tour} page={page} onPage={setPage} />}
      </RelationDrawer>
    </>
  );
}

function MediaGrid({ tour, page, onPage }: { tour: TourDetail; page: number; onPage: (p: number) => void }) {
  const q = useQuery({
    queryKey: tourMediaKey(tour.id, page),
    queryFn: () => toursApi.listTourMedia(tour.id, page, RELATION_PAGE_SIZE),
    placeholderData: keepPreviousData,
  });
  if (q.isLoading) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <CircularProgress size={14} aria-hidden /> Loading the media…
      </Typography>
    );
  }
  if (q.isError || !q.data) return <RelationFailure what="the media" onRetry={() => void q.refetch()} />;
  const rows = q.data.results;
  return (
    <>
      <Typography variant="body2" color="text.secondary">
        Photos and videos guests and staff shared in this tour’s chats. Thumbnails are downsized; click one for the original.
      </Typography>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No media shared yet.
        </Typography>
      ) : (
        <Box
          component="ul"
          aria-label="Tour media"
          sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1, gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))' }}
        >
          {rows.map((m) => (
            <MediaTile key={m.id} item={m} />
          ))}
        </Box>
      )}
      <RelationPager page={page} count={q.data.count} shown={rows.length} onPage={onPage} />
    </>
  );
}

const TILE_SX = { width: '100%', aspectRatio: '1 / 1', borderRadius: 1, border: 1, borderColor: 'divider', objectFit: 'cover', display: 'block' } as const;

function MediaTile({ item }: { item: TourMediaItem }) {
  const thumb = item.media_type === 'image' && item.image ? imageUrl({ p: item.image.p ?? '', s: item.image.s ?? '' }, IMAGE_PRESET.THUMB) : null;
  const who = item.sender?.name ?? null;
  const when = item.created_at ? formatRelativeTime(item.created_at) : null;
  const caption = [who, when].filter(Boolean).join(' · ');
  const label = `${item.media_type === 'video' ? 'Video' : 'Photo'}${who ? ` from ${who}` : ''} — open the original in a new tab`;
  const body = thumb ? (
    <Box component="img" src={thumb} alt="" loading="lazy" sx={TILE_SX} />
  ) : (
    <Box sx={{ ...TILE_SX, display: 'grid', placeItems: 'center', bgcolor: 'background.default', color: 'text.secondary' }}>
      <Box sx={{ display: 'grid', justifyItems: 'center', gap: 0.25 }}>
        {item.media_type === 'video' ? <PlayCircleOutlineIcon aria-hidden /> : <InsertDriveFileOutlinedIcon aria-hidden />}
        <Typography variant="caption">{item.media_type === 'video' ? 'Video' : 'File'}</Typography>
      </Box>
    </Box>
  );
  return (
    <Box component="li" data-media-item={item.id} sx={{ display: 'grid', gap: 0.25, minWidth: 0 }}>
      {item.url ? (
        <Box component="a" href={item.url} target="_blank" rel="noopener noreferrer" aria-label={label} sx={{ display: 'block' }}>
          {body}
        </Box>
      ) : (
        body
      )}
      {caption && (
        <Typography variant="caption" color="text.secondary" noWrap title={caption}>
          {caption}
        </Typography>
      )}
    </Box>
  );
}
